import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Card from '../../../shared/ui/card/card.jsx';
import AssignmentModal from '../../../shared/ui/assignment-modal/assignment-modal.jsx';
import LoadingContainer from '../../../shared/ui/loading-container/loading-container.jsx';
import PageHeader from '../../../shared/ui/page-header/page-header.jsx';

const STATUS_BADGE_CLASS = {
  new: 'bg-secondary-subtle text-secondary',
  pending_support: 'bg-warning-subtle text-warning',
  pending_client: 'bg-success-subtle text-success',
  closed: 'app-badge-disabled',
};

function formatDateTime(value) {
  const raw = String(value || '').trim();
  if (!raw) return '-';
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return raw;
  return date.toLocaleString();
}

function FileLinks({ files, onDownload }) {
  if (!files || files.length === 0) return null;
  return (
    <div className="mt-2 d-flex flex-wrap gap-2">
      {files.map((f) => (
        <button
          key={f.uid}
          type="button"
          className="btn btn-link btn-sm p-0 text-body-secondary text-decoration-none"
          onClick={() => onDownload(f.uid, f.name)}
        >
          <i className="ti ti-paperclip me-1" aria-hidden="true"></i>
          {f.name}
        </button>
      ))}
    </div>
  );
}

function avatarInitials(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return '?';
}

function CommentItem({ comment, onDownload, t }) {
  const bgClass = comment.isInternal
    ? 'bg-warning-subtle'
    : 'bg-primary-subtle';

  return (
    <div className="sl-item">
      <div className="sl-left">
        <div className={`sl-avatar ${bgClass}`}>
          {avatarInitials(comment.authorName)}
        </div>
      </div>
      <div className="sl-right">
        <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
          <span className="fw-semibold small">{comment.authorName}</span>
          {comment.isInternal ? (
            <span className="badge bg-warning-subtle text-warning">{t('support.badgeInternal')}</span>
          ) : null}
          <span className="sl-date ms-auto">{formatDateTime(comment.created)}</span>
        </div>
        <div className={`p-2 rounded-2 ${bgClass}`} style={{ whiteSpace: 'pre-wrap' }}>
          {comment.body}
        </div>
        <FileLinks files={comment.files} onDownload={onDownload} />
      </div>
    </div>
  );
}

function SupportTicketDetailsView({
  ticket,
  comments,
  isLoading,
  error,
  isStatusChanging,
  statusError,
  changeStatus,
  commentBody,
  commentIsInternal,
  commentSetPendingClient,
  isCommentSubmitting,
  commentError,
  onCommentBodyChange,
  onCommentIsInternalChange,
  onCommentSetPendingClientChange,
  onCommentFileChange,
  submitComment,
  isAssignModalOpen,
  adminProfileOptions,
  isAdminProfilesLoading,
  selectedManagerId,
  isAssignmentSaving,
  assignmentError,
  openAssignModal,
  closeAssignModal,
  onSelectManager,
  assignManager,
  unassignManager,
  downloadFile,
}) {
  const { t } = useTranslation();

  const STATUS_OPTIONS = [
    { value: 'new', label: t('support.statusNew') },
    { value: 'pending_support', label: t('support.statusInProgress') },
    { value: 'pending_client', label: t('support.statusAwaitingClient') },
    { value: 'closed', label: t('support.statusClosed') },
  ];

  const STATUS_LABELS = {
    new: t('support.statusNew'),
    pending_support: t('support.statusInProgress'),
    pending_client: t('support.statusAwaitingClient'),
    closed: t('support.statusClosed'),
  };

  const CATEGORY_LABELS = {
    general: t('support.categoryGeneral'),
    deposit_withdrawal: t('support.categoryDepositWithdrawal'),
    trading: t('support.categoryTrading'),
  };

  const PRIORITY_LABELS = {
    normal: t('support.priorityNormal'),
    urgent: t('support.priorityUrgent'),
  };

  function StatusBadge({ status }) {
    const label = STATUS_LABELS[status] || status || '-';
    const cls = STATUS_BADGE_CLASS[status] || 'bg-secondary-subtle text-secondary';
    return <span className={`badge ${cls}`}>{label}</span>;
  }

  const ticketSubtitle = ticket
    ? `#${ticket.number ?? '—'} · ${CATEGORY_LABELS[ticket.category] || ticket.category || '—'}`
    : '';

  return (
    <div>
      <div className="mb-3">
        <Link to="/support" className="btn btn-default btn-sm">
          <i className="ti ti-arrow-left me-1" aria-hidden="true"></i>
          {t('support.backToTickets')}
        </Link>
      </div>

      <LoadingContainer isLoading={isLoading} hasData={Boolean(ticket)} className="">
        {error ? <div className="alert alert-danger">{error}</div> : null}

        {ticket ? (
          <>
            <PageHeader
              title={ticket.subject}
              subtitle={ticketSubtitle}
            />

            <div className="row g-3">
              {/* Left: main content */}
              <div className="col-lg-8">
                {/* Conversation timeline */}
                <Card title={
                  <div className="d-flex align-items-center gap-2">
                    <StatusBadge status={ticket.status} />
                    {ticket.priority === 'urgent'
                      ? <span className="badge bg-danger-subtle text-danger">{t('support.badgeUrgent')}</span>
                      : null}
                  </div>
                }>
                  <div className="profiletimeline">
                    {/* Original message */}
                    <div className="sl-item">
                      <div className="sl-left">
                        <div className="sl-avatar bg-body-tertiary">
                          {avatarInitials(ticket.profileName || t('support.colClient'))}
                        </div>
                      </div>
                      <div className="sl-right">
                        <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                          <span className="fw-semibold small">{ticket.profileName || t('support.colClient')}</span>
                          <span className="sl-date ms-auto">{formatDateTime(ticket.created)}</span>
                        </div>
                        <div className="p-2 rounded-2 bg-body-tertiary" style={{ whiteSpace: 'pre-wrap' }}>
                          {ticket.body}
                        </div>
                        <FileLinks files={ticket.files} onDownload={downloadFile} />
                      </div>
                    </div>

                    {/* Comments */}
                    {comments.map((c) => (
                      <CommentItem key={c.uid} comment={c} onDownload={downloadFile} t={t} />
                    ))}
                  </div>
                </Card>

                {/* Add comment */}
                <Card title={t('support.addReply')}>
                  {commentError ? <div className="alert alert-danger">{commentError}</div> : null}

                  <div className="mb-3">
                    <textarea
                      className="form-control"
                      rows={4}
                      value={commentBody}
                      onChange={(event) => onCommentBodyChange(event.target.value)}
                      disabled={isCommentSubmitting}
                      placeholder={t('support.replyPlaceholder')}
                    />
                  </div>

                  <div className="mb-3">
                    <input
                      type="file"
                      className="form-control"
                      onChange={(event) => onCommentFileChange(event.target.files?.[0] ?? null)}
                      disabled={isCommentSubmitting}
                    />
                  </div>

                  <div className="d-flex flex-wrap gap-3 align-items-center mb-3">
                    <div className="form-check form-switch mb-0">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="commentIsInternal"
                        checked={commentIsInternal}
                        onChange={(event) => onCommentIsInternalChange(event.target.checked)}
                        disabled={isCommentSubmitting}
                      />
                      <label className="form-check-label" htmlFor="commentIsInternal">
                        {t('support.internalNote')}
                      </label>
                    </div>
                    <div className="form-check form-switch mb-0">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="commentSetPendingClient"
                        checked={commentSetPendingClient}
                        onChange={(event) => onCommentSetPendingClientChange(event.target.checked)}
                        disabled={isCommentSubmitting}
                      />
                      <label className="form-check-label" htmlFor="commentSetPendingClient">
                        {t('support.setPendingClient')}
                      </label>
                    </div>
                  </div>

                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={submitComment}
                    disabled={isCommentSubmitting || !commentBody.trim()}
                  >
                    {isCommentSubmitting ? t('support.sending') : t('support.sendReply')}
                  </button>
                </Card>
              </div>

              {/* Right: sidebar */}
              <div className="col-lg-4">
                <Card title={t('support.sidebarStatus')}>
                  {statusError ? <div className="alert alert-danger py-2 mb-2">{statusError}</div> : null}
                  <select
                    className="form-select"
                    value={ticket.status}
                    onChange={(event) => changeStatus(event.target.value)}
                    disabled={isStatusChanging}
                  >
                    {STATUS_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </Card>

                <Card title={t('support.sidebarClient')}>
                  {ticket.profileId ? (
                    <Link to={`/profiles/${ticket.profileId}`} className="text-decoration-none">
                      {ticket.profileName}
                    </Link>
                  ) : (
                    <span className="text-muted">—</span>
                  )}
                </Card>

                <Card title={t('support.sidebarAssignedTo')}>
                  <p className="mb-2 small">{ticket.assignedName || <span className="text-muted">{t('support.notAssigned')}</span>}</p>
                  <button
                    type="button"
                    className="btn btn-sm btn-default"
                    onClick={openAssignModal}
                  >
                    <i className="ti ti-user-plus me-1" aria-hidden="true"></i>
                    {ticket.assignedName ? t('support.reassign') : t('support.assign')}
                  </button>
                </Card>

                <Card title={t('support.sidebarDetails')}>
                  <dl className="app-kv mb-0">
                    <div className="app-kv-item">
                      <dt>{t('support.priority')}</dt>
                      <dd>{PRIORITY_LABELS[ticket.priority] || ticket.priority || '—'}</dd>
                    </div>
                    <div className="app-kv-item">
                      <dt>{t('support.created')}</dt>
                      <dd>{formatDateTime(ticket.created)}</dd>
                    </div>
                    {ticket.updated ? (
                      <div className="app-kv-item">
                        <dt>{t('support.updated')}</dt>
                        <dd>{formatDateTime(ticket.updated)}</dd>
                      </div>
                    ) : null}
                  </dl>
                </Card>
              </div>
            </div>
          </>
        ) : null}
      </LoadingContainer>

      <AssignmentModal
        isOpen={isAssignModalOpen}
        title={t('support.assignTicket')}
        targetLabel={t('support.colSubject')}
        targetName={ticket?.subject || '-'}
        error={assignmentError}
        options={adminProfileOptions}
        isOptionsLoading={isAdminProfilesLoading}
        selectedId={selectedManagerId}
        isSaving={isAssignmentSaving}
        showUnassign={Boolean(ticket?.assignedId)}
        onClose={closeAssignModal}
        onSelect={onSelectManager}
        onAssign={assignManager}
        onUnassign={unassignManager}
      />
    </div>
  );
}

export default SupportTicketDetailsView;
